import { useMemo } from 'react';
import { Avatar, Badge, Card, PulseDot } from '../ui';
import { formatDate } from '../../utils/dateUtils';
import AiQueueCards from '../ai/AiQueueCards';
import type { AiJob } from '../../lib/aiJobs';

interface RailTask {
  id: string;
  title: string;
  status: string;
  due_date?: string;
  client_name?: string;
  assigned_to?: string;
}

interface RailUser {
  id: string;
  name: string;
}

interface FulfillmentRailProps {
  tasks: RailTask[];
  users: RailUser[];
  selectedUserId: string | null;
  selectedUserName: string;
  onOpenTask: (task: RailTask) => void;
  /** Open an agent queue job in the same detail panel the Agents screen uses. */
  onOpenJob: (job: AiJob) => void;
}

const STATUS_SEGMENTS: Array<{ status: string; label: string; color: string }> = [
  { status: 'Submitted', label: 'Done', color: 'var(--status-up)' },
  { status: 'Complete', label: 'Needs Review', color: 'var(--blue-400)' },
  { status: 'In Progress', label: 'In Progress', color: 'var(--status-warn)' },
  { status: 'Blocked', label: 'Blocked', color: 'var(--status-down)' },
  { status: 'Scheduled', label: 'Scheduled', color: 'var(--status-idle)' },
];

function railBar(value: number, max: number, color: string) {
  return (
    <span
      style={{
        flex: 1,
        height: '5px',
        borderRadius: '3px',
        background: 'var(--surface-active)',
        overflow: 'hidden',
        display: 'inline-block',
      }}
    >
      <span
        style={{
          display: 'block',
          width: max > 0 ? `${Math.max(3, (value / max) * 100)}%` : '0%',
          height: '100%',
          borderRadius: '3px',
          background: color,
          transition: 'width var(--dur-page) var(--ease-out)',
        }}
      />
    </span>
  );
}

export default function FulfillmentRail({ tasks, users, selectedUserId, selectedUserName, onOpenTask, onOpenJob }: FulfillmentRailProps) {
  const stats = useMemo(() => {
    const total = tasks.length;
    // Complete counts toward done here — the work is off the plate, just awaiting review before submission
    const isCleared = (t: RailTask) => t.status === 'Complete' || t.status === 'Submitted';
    const done = tasks.filter(isCleared).length;
    const awaitingReview = tasks.filter((t) => t.status === 'Complete').length;
    const pct = total > 0 ? Math.round((done / total) * 100) : 0;

    const byStatus = STATUS_SEGMENTS.map((s) => ({
      ...s,
      count: tasks.filter((t) => t.status === s.status).length,
    })).filter((s) => s.count > 0);

    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const attention = tasks
      .filter(
        (t) =>
          !isCleared(t) &&
          (t.status === 'Blocked' || (t.due_date && new Date(t.due_date) < startOfToday))
      )
      .sort((a, b) => (a.due_date || '9999').localeCompare(b.due_date || '9999'))
      .slice(0, 5);

    const openByClient = new Map<string, number>();
    for (const t of tasks) {
      if (isCleared(t)) continue;
      const name = t.client_name || 'Unknown Client';
      openByClient.set(name, (openByClient.get(name) || 0) + 1);
    }
    const clients = [...openByClient.entries()].sort((a, b) => b[1] - a[1]).slice(0, 7);

    const openByUser = new Map<string, number>();
    for (const t of tasks) {
      if (isCleared(t) || !t.assigned_to) continue;
      openByUser.set(t.assigned_to, (openByUser.get(t.assigned_to) || 0) + 1);
    }
    const team = users
      .map((u) => ({ user: u, count: openByUser.get(u.id) || 0 }))
      .filter((e) => e.count > 0)
      .sort((a, b) => b.count - a.count);

    return { total, done, pct, awaitingReview, byStatus, attention, clients, team };
  }, [tasks, users]);

  const firstName = selectedUserName.split(' ')[0];
  const isTeamView = !selectedUserId;
  const maxClient = stats.clients.length > 0 ? stats.clients[0][1] : 0;
  const maxTeam = stats.team.length > 0 ? stats.team[0].count : 0;

  return (
    <div className="ck-stagger" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <AiQueueCards onOpenJob={onOpenJob} />

      {/* ---- Queue progress ---- */}
      <Card
        eyebrow="Queue Progress"
        title={isTeamView ? "The team's plate" : `${firstName}'s plate`}
        actions={
          stats.total > 0 ? (
            <span
              className="t-num"
              style={{
                fontSize: 'var(--text-2xl)',
                fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
                letterSpacing: 'var(--tracking-tight)',
                lineHeight: 1,
                color: stats.pct === 100 ? 'var(--status-up)' : 'var(--text-body)',
              }}
            >
              {stats.pct}%
            </span>
          ) : undefined
        }
      >
        {stats.total === 0 ? (
          <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>
            No tasks in this view — adjust the filters or enjoy the quiet.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* big momentum bar — done includes complete (off the plate, awaiting review) */}
            <div>
              <div
                style={{
                  position: 'relative',
                  height: '10px',
                  borderRadius: '5px',
                  background: 'var(--surface-active)',
                  overflow: 'hidden',
                }}
              >
                <span
                  style={{
                    position: 'absolute',
                    inset: '0 auto 0 0',
                    width: `${stats.pct}%`,
                    borderRadius: '5px',
                    background: 'var(--status-up)',
                    transition: 'width var(--dur-page) var(--ease-out)',
                  }}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '7px' }}>
                <span className="t-num" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                  {stats.done} of {stats.total} done
                </span>
                {stats.awaitingReview > 0 ? (
                  <span className="t-num" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
                    {stats.awaitingReview} awaiting review
                  </span>
                ) : null}
              </div>
            </div>

            {/* status composition */}
            <div>
              <div style={{ display: 'flex', height: '6px', borderRadius: '3px', overflow: 'hidden', gap: '2px' }}>
                {stats.byStatus.map((s) => (
                  <span
                    key={s.status}
                    title={`${s.label}: ${s.count}`}
                    style={{
                      width: `${(s.count / stats.total) * 100}%`,
                      background: s.color,
                      transition: 'width var(--dur-page) var(--ease-out)',
                    }}
                  />
                ))}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', marginTop: '10px' }}>
                {stats.byStatus.map((s) => (
                  <span
                    key={s.status}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}
                  >
                    <span style={{ width: '8px', height: '8px', borderRadius: '2px', background: s.color, flexShrink: 0 }} />
                    {s.label}
                    <span className="t-num" style={{ color: 'var(--text-faint)' }}>{s.count}</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* ---- Needs attention ---- */}
      {stats.attention.length > 0 ? (
        <Card eyebrow="Needs Attention" title={`${stats.attention.length} flagged`}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {stats.attention.map((t) => {
              const isBlocked = t.status === 'Blocked';
              return (
                <div
                  key={t.id}
                  className="hover-wash pressable"
                  onClick={() => onOpenTask(t)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '8px 10px',
                    margin: '0 -10px',
                    borderRadius: 'var(--radius-sm)',
                    cursor: 'pointer',
                  }}
                >
                  <PulseDot tone={isBlocked ? 'down' : 'warn'} title={isBlocked ? 'Blocked' : 'Past due'} />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span
                      style={{
                        display: 'block',
                        fontSize: 'var(--text-sm)',
                        color: 'var(--text-body)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {t.title}
                    </span>
                    <span style={{ display: 'block', fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
                      {t.client_name || 'Unknown Client'}
                    </span>
                  </span>
                  {isBlocked ? (
                    <Badge tone="down">Blocked</Badge>
                  ) : (
                    <span className="t-num" style={{ fontSize: 'var(--text-xs)', color: 'var(--status-warn)', flexShrink: 0 }}>
                      due {t.due_date ? formatDate(t.due_date, 'MMM d') : '—'}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      ) : null}

      {/* ---- Workload by client ---- */}
      {stats.clients.length > 0 ? (
        <Card eyebrow="Workload" title="Open tasks by client">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            {stats.clients.map(([name, count]) => (
              <div
                key={name}
                className="hover-wash"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '7px 10px',
                  margin: '0 -10px',
                  borderRadius: 'var(--radius-sm)',
                }}
              >
                <span
                  style={{
                    width: '38%',
                    minWidth: 0,
                    fontSize: 'var(--text-sm)',
                    color: 'var(--text-body)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    flexShrink: 0,
                  }}
                >
                  {name}
                </span>
                {railBar(count, maxClient, 'var(--chart-1)')}
                <span className="t-num" style={{ width: '24px', textAlign: 'right', fontSize: 'var(--text-xs)', color: 'var(--text-muted)', flexShrink: 0 }}>
                  {count}
                </span>
              </div>
            ))}
          </div>
        </Card>
      ) : null}

      {/* ---- Team load (All Users view) ---- */}
      {isTeamView && stats.team.length > 0 ? (
        <Card eyebrow="Team Load" title="Open tasks by person">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            {stats.team.map(({ user, count }) => (
              <div
                key={user.id}
                className="hover-wash"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '7px 10px',
                  margin: '0 -10px',
                  borderRadius: 'var(--radius-sm)',
                }}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '9px', width: '38%', minWidth: 0, flexShrink: 0 }}>
                  <Avatar name={user.name} size={22} />
                  <span
                    style={{
                      fontSize: 'var(--text-sm)',
                      color: 'var(--text-body)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {user.name.split(' ')[0]}
                  </span>
                </span>
                {railBar(count, maxTeam, 'var(--chart-4)')}
                <span className="t-num" style={{ width: '24px', textAlign: 'right', fontSize: 'var(--text-xs)', color: 'var(--text-muted)', flexShrink: 0 }}>
                  {count}
                </span>
              </div>
            ))}
          </div>
        </Card>
      ) : null}
    </div>
  );
}
